// Sustainable Handyman Services - Standardized Constants
// This file centralizes all status values, categories, and formatting standards

// ============== JOB/ENQUIRY STATUSES ==============
export const JOB_STATUSES = {
  new: { label: 'New', color: '#3B82F6', bgColor: 'bg-blue-100 text-blue-800', order: 1 },
  site_visit: { label: 'Site Visit', color: '#8B5CF6', bgColor: 'bg-purple-100 text-purple-800', order: 2 },
  quoted: { label: 'Quoted', color: '#F59E0B', bgColor: 'bg-amber-100 text-amber-800', order: 3 },
  accepted: { label: 'Accepted', color: '#10B981', bgColor: 'bg-green-100 text-green-800', order: 4 },
  scheduled: { label: 'Scheduled', color: '#06B6D4', bgColor: 'bg-cyan-100 text-cyan-800', order: 5 },
  in_progress: { label: 'In Progress', color: '#6366F1', bgColor: 'bg-indigo-100 text-indigo-800', order: 6 },
  completed: { label: 'Completed', color: '#22C55E', bgColor: 'bg-emerald-100 text-emerald-800', order: 7 },
  invoiced: { label: 'Invoiced', color: '#EC4899', bgColor: 'bg-pink-100 text-pink-800', order: 8 },
  paid: { label: 'Paid', color: '#14B8A6', bgColor: 'bg-teal-100 text-teal-800', order: 9 },
  rejected: { label: 'Rejected', color: '#EF4444', bgColor: 'bg-red-100 text-red-800', order: 10 },
  on_hold: { label: 'On Hold', color: '#6B7280', bgColor: 'bg-gray-200 text-gray-600', order: 11 },
  cancelled: { label: 'Cancelled', color: '#9CA3AF', bgColor: 'bg-gray-100 text-gray-500', order: 12 },
};

// Pipeline stages as array (sorted by order)
export const PIPELINE_STAGES = Object.entries(JOB_STATUSES)
  .map(([id, config]) => ({ id, ...config }))
  .sort((a, b) => a.order - b.order);

// Canonical status groupings — keep in sync with backend/constants.py
export const ACTIVE_JOB_STATUSES = ['accepted', 'scheduled', 'in_progress', 'site_visit'];
export const UPCOMING_JOB_STATUSES = ['new', 'site_visit', 'quoted', 'accepted', 'scheduled', 'in_progress'];
export const PAST_JOB_STATUSES = ['paid', 'completed', 'cancelled'];

// ============== QUOTE STATUSES ==============
export const QUOTE_STATUSES = {
  draft: { label: 'Draft', color: '#9CA3AF', bgColor: 'bg-gray-100 text-gray-600', order: 1 },
  sent: { label: 'Sent', color: '#3B82F6', bgColor: 'bg-blue-100 text-blue-800', order: 2 },
  accepted: { label: 'Accepted', color: '#10B981', bgColor: 'bg-green-100 text-green-800', order: 3 },
  rejected: { label: 'Rejected', color: '#EF4444', bgColor: 'bg-red-100 text-red-800', order: 4 },
  expired: { label: 'Expired', color: '#6B7280', bgColor: 'bg-gray-200 text-gray-600', order: 5 },
  converted: { label: 'Converted', color: '#14B8A6', bgColor: 'bg-teal-100 text-teal-800', order: 6 },
};

// ============== INVOICE STATUSES ==============
export const INVOICE_STATUSES = {
  draft: { label: 'Draft', color: '#9CA3AF', bgColor: 'bg-gray-100 text-gray-600', order: 1 },
  sent: { label: 'Sent', color: '#3B82F6', bgColor: 'bg-blue-100 text-blue-800', order: 2 },
  overdue: { label: 'Overdue', color: '#EF4444', bgColor: 'bg-red-100 text-red-800', order: 3 },
  part_paid: { label: 'Part Paid', color: '#F59E0B', bgColor: 'bg-amber-100 text-amber-800', order: 4 },
  paid: { label: 'Paid', color: '#10B981', bgColor: 'bg-green-100 text-green-800', order: 5 },
  cancelled: { label: 'Cancelled', color: '#6B7280', bgColor: 'bg-gray-200 text-gray-600', order: 6 },
};

// ============== SERVICE CATEGORIES ==============
export const SERVICE_CATEGORIES = {
  carpentry: { label: 'Carpentry', color: '#8B4513' },
  general_repairs: { label: 'General Repairs', color: '#4A90A4' },
  furniture_builds: { label: 'Furniture Builds', color: '#6B4423' },
  cnc_factory: { label: 'CNC/Factory Work', color: '#708090' },
};

// ============== PRICING STRATEGY ==============
export const PRICING = {
  DAY_RATES: {
    1: 300,  // £300/day for 1 person
    2: 450,  // £450/day for 2 people
  },
  MULTI_DAY_DISCOUNTS: {
    3: 5,    // 5% off for 3+ days
    5: 10,   // 10% off for 5+ days
    10: 15,  // 15% off for 10+ days
  },
  DEFAULT_TEAM_SIZE: 1,
};

// Calculate job price based on days and team size
export const calculateJobPrice = (numDays, teamSize = 1, customDiscount = null) => {
  const dayRate = PRICING.DAY_RATES[teamSize] || PRICING.DAY_RATES[1];
  const subtotal = dayRate * numDays;
  
  // Determine discount percentage
  let discountPercent = customDiscount;
  if (discountPercent === null) {
    // Auto-calculate based on number of days
    const discountTiers = Object.entries(PRICING.MULTI_DAY_DISCOUNTS)
      .map(([days, discount]) => ({ days: parseInt(days), discount }))
      .sort((a, b) => b.days - a.days);
    
    for (const tier of discountTiers) {
      if (numDays >= tier.days) {
        discountPercent = tier.discount;
        break;
      }
    }
  }
  
  const discountAmount = discountPercent ? (subtotal * discountPercent / 100) : 0;
  const total = subtotal - discountAmount;
  
  return {
    dayRate,
    numDays,
    teamSize,
    subtotal,
    discountPercent: discountPercent || 0,
    discountAmount: Math.round(discountAmount * 100) / 100,
    total: Math.round(total * 100) / 100,
  };
};

// Aliases for backward compatibility
const SERVICE_ALIASES = {
  'general-repairs': 'general_repairs',
  'furniture-builds': 'furniture_builds',
  'cnc-factory': 'cnc_factory',
  'cnc/factory': 'cnc_factory',
};

export const normalizeServiceCategory = (service) => {
  if (!service) return 'general_repairs';
  const normalized = service.toLowerCase().trim();
  return SERVICE_ALIASES[normalized] || normalized;
};

export const getServiceLabel = (service) => {
  const normalized = normalizeServiceCategory(service);
  return SERVICE_CATEGORIES[normalized]?.label || service;
};

// ============== SOURCE LABELS ==============
export const SOURCE_LABELS = {
  website: { label: 'Website', color: '#3B82F6', bgColor: 'bg-blue-100 text-blue-800' },
  whatsapp: { label: 'WhatsApp', color: '#25D366', bgColor: 'bg-green-100 text-green-800' },
  phone: { label: 'Phone Call', color: '#6366F1', bgColor: 'bg-indigo-100 text-indigo-800' },
  email: { label: 'Email', color: '#EC4899', bgColor: 'bg-pink-100 text-pink-800' },
  facebook: { label: 'Facebook', color: '#1877F2', bgColor: 'bg-blue-100 text-blue-800' },
  referral: { label: 'Referral', color: '#10B981', bgColor: 'bg-emerald-100 text-emerald-800' },
  google: { label: 'Google', color: '#EA4335', bgColor: 'bg-red-100 text-red-800' },
  other: { label: 'Other', color: '#6B7280', bgColor: 'bg-gray-100 text-gray-600' },
  // Iter 61: Humanized labels for back-end tags that previously rendered raw.
  from_invoice: { label: 'Historical Project', color: '#78350F', bgColor: 'bg-amber-100 text-amber-900' },
  invoice_backfill: { label: 'Historical Project', color: '#78350F', bgColor: 'bg-amber-100 text-amber-900' },
  invoice_ocr: { label: 'Uploaded Invoice', color: '#78350F', bgColor: 'bg-amber-100 text-amber-900' },
  import: { label: 'Historical Project', color: '#78350F', bgColor: 'bg-amber-100 text-amber-900' },
  in_person: { label: 'Walk-in', color: '#059669', bgColor: 'bg-emerald-100 text-emerald-800' },
};

// ============== REVIEW STATUSES ==============
export const REVIEW_STATUSES = {
  not_requested: { label: 'Not Requested', color: '#9CA3AF', bgColor: 'bg-gray-100 text-gray-600' },
  requested: { label: 'Requested', color: '#F59E0B', bgColor: 'bg-amber-100 text-amber-800' },
  received: { label: 'Received', color: '#3B82F6', bgColor: 'bg-blue-100 text-blue-800' },
  published: { label: 'Published', color: '#10B981', bgColor: 'bg-green-100 text-green-800' },
};

// ============== JOB TAGS ==============
export const JOB_TAGS = {
  site_visit_needed: { label: 'Site Visit Needed', color: '#8B5CF6', bgColor: 'bg-purple-100 text-purple-800' },
  waiting_customer: { label: 'Waiting for Customer', color: '#F59E0B', bgColor: 'bg-amber-100 text-amber-800' },
  materials_needed: { label: 'Materials Needed', color: '#EF4444', bgColor: 'bg-red-100 text-red-800' },
  deposit_received: { label: 'Deposit Received', color: '#10B981', bgColor: 'bg-green-100 text-green-800' },
  photos_needed: { label: 'Photos Needed', color: '#EC4899', bgColor: 'bg-pink-100 text-pink-800' },
  review_requested: { label: 'Review Requested', color: '#3B82F6', bgColor: 'bg-blue-100 text-blue-800' },
  review_received: { label: 'Review Received', color: '#059669', bgColor: 'bg-emerald-100 text-emerald-800' },
  urgent: { label: 'Urgent', color: '#DC2626', bgColor: 'bg-red-200 text-red-900' },
  recurring: { label: 'Recurring', color: '#6366F1', bgColor: 'bg-indigo-100 text-indigo-800' },
};

// ============== PAYMENT STATUSES ==============
export const PAYMENT_STATUSES = {
  unpaid: { label: 'Unpaid', color: '#EF4444', bgColor: 'bg-red-100 text-red-800' },
  deposit_paid: { label: 'Deposit Paid', color: '#F59E0B', bgColor: 'bg-amber-100 text-amber-800' },
  part_paid: { label: 'Part Paid', color: '#F59E0B', bgColor: 'bg-amber-100 text-amber-800' },
  paid: { label: 'Paid', color: '#10B981', bgColor: 'bg-green-100 text-green-800' },
};

// ============== PRIORITY LEVELS ==============
export const PRIORITY_LEVELS = {
  low: { label: 'Low', color: '#9CA3AF', bgColor: 'bg-gray-100 text-gray-600', order: 1 },
  normal: { label: 'Normal', color: '#3B82F6', bgColor: 'bg-blue-100 text-blue-800', order: 2 },
  high: { label: 'High', color: '#F59E0B', bgColor: 'bg-amber-100 text-amber-800', order: 3 },
  urgent: { label: 'Urgent', color: '#EF4444', bgColor: 'bg-red-100 text-red-800', order: 4 },
};

// ============== DATE FORMATTING ==============
/**
 * Format a date to standard format: "07 Mar 2026"
 * @param {string|Date} dateValue - Date to format
 * @param {boolean} longFormat - Use "07 March 2026" instead
 * @returns {string|null}
 */
export const formatDate = (dateValue, longFormat = false) => {
  if (!dateValue) return null;
  
  try {
    const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    
    if (isNaN(date.getTime())) return null;
    
    const options = {
      day: 'numeric',
      month: longFormat ? 'long' : 'short',
      year: 'numeric'
    };
    
    // Format to "7 Mar 2026" or "7 March 2026"
    return date.toLocaleDateString('en-GB', options);
  } catch {
    return null;
  }
};

/**
 * Format a datetime to standard format: "07 Mar 2026 14:30"
 * @param {string|Date} dateValue - Datetime to format
 * @returns {string|null}
 */
export const formatDateTime = (dateValue) => {
  if (!dateValue) return null;
  
  try {
    const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    
    if (isNaN(date.getTime())) return null;
    
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return null;
  }
};

// ============== STATUS NORMALIZATION ==============
const STATUS_MIGRATION_MAP = {
  'pending': 'new',
  'done': 'completed',
  'in-progress': 'in_progress',
  'inspection_scheduled': 'site_visit',
};

/**
 * Normalize a job status to the standard value
 * @param {string} status - Status to normalize
 * @returns {string}
 */
export const normalizeJobStatus = (status) => {
  if (!status) return 'new';
  const normalized = status.toLowerCase().trim();
  return STATUS_MIGRATION_MAP[normalized] || normalized;
};

/**
 * Get status display info (label, color, bgColor)
 * @param {string} status - Status ID
 * @param {string} type - 'job', 'quote', 'invoice', 'review'
 * @returns {object}
 */
export const getStatusInfo = (status, type = 'job') => {
  const normalized = normalizeJobStatus(status);
  
  const statusMaps = {
    job: JOB_STATUSES,
    quote: QUOTE_STATUSES,
    invoice: INVOICE_STATUSES,
    review: REVIEW_STATUSES,
  };
  
  const map = statusMaps[type] || JOB_STATUSES;
  return map[normalized] || { label: status, color: '#6B7280', bgColor: 'bg-gray-100 text-gray-600' };
};

// ============== NEXT ACTION LOGIC ==============
export const NEXT_ACTIONS = {
  new: { action: 'Schedule site visit or create quote', icon: 'Calendar' },
  site_visit: { action: 'Create quote', icon: 'FileText' },
  quoted: { action: 'Follow up on quote', icon: 'Phone' },
  accepted: { action: 'Schedule job', icon: 'Calendar' },
  scheduled: { action: 'Start job', icon: 'Play' },
  in_progress: { action: 'Mark complete', icon: 'Check' },
  completed: { action: 'Create invoice', icon: 'FileText' },
  invoiced: { action: 'Chase payment', icon: 'PoundSterling' },
  paid: { action: 'Request review', icon: 'Star' },
};

/**
 * Get the suggested next action based on current status
 * @param {string} status - Current job status
 * @returns {object} - { action: string, icon: string }
 */
export const getNextAction = (status) => {
  const normalized = normalizeJobStatus(status);
  return NEXT_ACTIONS[normalized] || { action: 'Review', icon: 'Eye' };
};

// ============== HELPER: Get all options for select dropdowns ==============
export const getJobStatusOptions = () => PIPELINE_STAGES.map(s => ({ value: s.id, label: s.label }));
export const getQuoteStatusOptions = () => Object.entries(QUOTE_STATUSES).map(([id, config]) => ({ value: id, label: config.label }));
export const getInvoiceStatusOptions = () => Object.entries(INVOICE_STATUSES).map(([id, config]) => ({ value: id, label: config.label }));
export const getServiceOptions = () => Object.entries(SERVICE_CATEGORIES).map(([id, config]) => ({ value: id, label: config.label }));
export const getSourceOptions = () => Object.entries(SOURCE_LABELS).map(([id, config]) => ({ value: id, label: config.label }));
export const getPriorityOptions = () => Object.entries(PRIORITY_LEVELS)
  .sort((a, b) => a[1].order - b[1].order)
  .map(([id, config]) => ({ value: id, label: config.label }));
export const getTagOptions = () => Object.entries(JOB_TAGS).map(([id, config]) => ({ value: id, label: config.label }));
